import { memo, useEffect, useRef } from 'react';

import cn from 'classnames';

import { OPopper } from '@src/shared';

import { useWhiteboardContext } from '@src/contexts';

import { HiPencil } from 'react-icons/hi2';
import { RiCursorFill } from 'react-icons/ri';
import { PiGridFourLight } from 'react-icons/pi';
import { PiCircleHalfFill } from 'react-icons/pi';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { FaArrowRotateLeft } from 'react-icons/fa6';
import { FaLock } from 'react-icons/fa';

import './style.scss';

type TWhiteBoardWidth = 'full' | 'default';

const Whiteboard = ({ width = 'default', style }: { width?: TWhiteBoardWidth; style?: React.CSSProperties }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    whiteboardIsDrawingMode,
    whiteboardAction,
    debouncedBrushColor,
    debouncedchangeCanvasBackgroundColor,
    whiteboardAddObj,
    whiteboardIsEraser,
    wbIsTransparent,
    isGridVisible,
    wbIsDrawing,
    parentElement,
    InitWhiteboard,
    wbIsLock,
    isHostUser,
    wbCanvasSaveImg,
    theme,
  } = useWhiteboardContext();

  useEffect(() => {
    setTimeout(() => {
      if (canvasRef.current) {
        InitWhiteboard();
      }
      const defaultBrushColor = theme === 'dark' ? 'white' : 'black';
      debouncedBrushColor(defaultBrushColor);
    }, 0);
  }, [theme]);

  return (
    <div
      style={{ ...(style || {}) }}
      className={cn('whiteboard-container', {
        [width]: width,
      })}
    >
      <div
        className={`whiteboard-container-inner ${wbIsTransparent ? 'whiteboard--bg__Transparent' : 'whiteboard-bg'}`}
      >
        <div className="whiteboard-actions-container">
          <button
            className={`whiteboard-action  ${wbIsTransparent ? 'active' : ''} `}
            onClick={() => {
              if (!isHostUser) {
                if (wbIsLock) {
                  return;
                }
              }
              whiteboardAction({ action: 'transparent' }, true);
            }}
          >
            <PiCircleHalfFill className="action-icon " />
          </button>
          <button
            className={`whiteboard-action  ${isGridVisible ? 'active' : ''} `}
            onClick={() => {
              if (!isHostUser) {
                if (wbIsLock) {
                  return;
                }
              }
              whiteboardAction({ action: 'grid' }, true);
            }}
          >
            <PiGridFourLight className="action-icon" />
          </button>
          <input
            onChange={(ev) => debouncedBrushColor(ev.target.value)}
            className="whiteboardColorPicker "
            type="color"
            defaultValue="#FFFFFF"
          />
          <input
            onChange={(ev) => debouncedchangeCanvasBackgroundColor(ev.target.value, true)}
            className="whiteboardColorPicker"
            type="color"
            defaultValue="#000e16"
          />
          <button
            className={`whiteboard-action  ${wbIsDrawing ? 'active' : ''} `}
            onClick={() => {
              whiteboardIsDrawingMode(true);
            }}
          >
            <HiPencil className="action-icon" />
          </button>
          <button
            className={`whiteboard-action  ${!wbIsDrawing ? 'active' : ''} `}
            onClick={() => {
              whiteboardIsDrawingMode(false);
            }}
          >
            <RiCursorFill className="action-icon" />
          </button>
          <button
            className="whiteboard-action "
            onClick={() => {
              if (!isHostUser) {
                if (wbIsLock) {
                  return;
                }
              }
              whiteboardAction({ action: 'undo' }, true);
            }}
          >
            <FaArrowRotateLeft className="action-icon" />
          </button>
          <button
            className="whiteboard-action whiteboard-action-redo "
            onClick={() => {
              if (!isHostUser) {
                if (wbIsLock) {
                  return;
                }
              }
              whiteboardAction({ action: 'redo' }, true);
            }}
          >
            <FaArrowRotateLeft className="action-icon" />
          </button>

          <OPopper
            customStyles={{
              root: {
                padding: '0px',
                backgroundColor: '#191D1D',
                width: '160px',
              },
            }}
            classes={{ containerStyle: 'whiteboard-action' }}
            position="bottom-end"
            icon={<BiDotsVerticalRounded className="action-icon" />}
          >
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardAddObj('text');
              }}
            >
              Add text
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardAddObj('line');
              }}
            >
              Add line
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardAddObj('rect');
              }}
            >
              Add rectangle
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardAddObj('triangle');
              }}
            >
              Add triangle
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardAddObj('circle');
              }}
            >
              Add circle
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                whiteboardIsEraser(true);
              }}
            >
              Eraser
            </div>
            <div
              className="dropdown-action"
              onClick={() => {
                if (!isHostUser) {
                  if (wbIsLock) {
                    return;
                  }
                }
                whiteboardAction({ action: 'clear' }, true);
              }}
            >
              Clean
            </div>
            {isHostUser && (
              <div className="dropdown-action" onClick={wbCanvasSaveImg}>
                Save
              </div>
            )}
          </OPopper>

          <button
            className={`whiteboard-action  ${wbIsLock ? 'active' : ''} `}
            onClick={() => {
              if (!isHostUser) {
                return;
              }
              whiteboardAction({ action: !wbIsLock ? 'lock' : 'unlock' }, true);
            }}
          >
            <FaLock className="action-icon" />
          </button>
        </div>
        <div className="whiteboard-canvas-container" ref={parentElement}>
          <canvas id="wbCanvasV2" ref={canvasRef} />
        </div>
      </div>
    </div>
  );
};

export default memo(Whiteboard);
